import React, { useState, useEffect, useRef } from 'react';
import * as api from '../../api/user-app-api.js';
import { Button } from '../common/Button';

export const AuthScreen = ({ setView, setIsLoggedIn, setUserId, setUser }) => {
    const [step, setStep] = useState('enterPhone');
    const [phone, setPhone] = useState('');
    const [otp, setOtp] = useState(Array(6).fill(''));
    const [serverOtp, setServerOtp] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [logo, setLogo] = useState(null);
    const [logoLoading, setLogoLoading] = useState(true);

    const inputsRef = useRef([]);

    // Fetch logo
    useEffect(() => {
        const fetchLogo = async () => {
            try {
                setLogoLoading(true);
                const res = await api.fetchSettings();
                if (res.data && res.data.logo_url) {
                    setLogo(res.data.logo_url);
                }
            } catch (e) {
                console.error('Failed to fetch logo:', e);
            } finally {
                setLogoLoading(false);
            }
        };
        fetchLogo();
    }, []);

    const handleSendOtp = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await api.sendUserOTP({ phone });
            setServerOtp(res.data.otp);
            setStep('verifyOTP');
        } catch (e) {
            setError(e.response?.data?.message || 'Failed to send OTP');
        } finally {
            setLoading(false);
        }
    };

    const handleOtpChange = (value, index) => {
        if (!/^\d?$/.test(value)) return;

        const newOtp = [...otp];
        newOtp[index] = value;
        setOtp(newOtp);

        if (value && index < 5) {
            inputsRef.current[index + 1]?.focus();
        }
    };

    const handleOtpKeyDown = (e, index) => {
        if (e.key === "Backspace" && !otp[index] && index > 0) {
            inputsRef.current[index - 1]?.focus();
        }
    };

    const handleVerifyOtp = async () => {
        setLoading(true);
        setError(null);
        try {
            const finalOtp = otp.join('');
            const res = await api.verifyUserOTP({ phone, otp: finalOtp });
            const { token, user } = res.data;
            const isNewlyRegistered =
                Boolean(
                    res.data?.is_new_user ??
                    res.data?.is_new ??
                    user?.is_new_user ??
                    user?.is_new ??
                    user?.isNewUser
                ) || !user?.name;

            localStorage.setItem('authToken', token);
            localStorage.setItem('userId', user.id);
            localStorage.setItem('isLoggedIn', 'true');
            if (isNewlyRegistered) {
                localStorage.setItem('needsProfileCompletion', 'true');
            } else {
                localStorage.removeItem('needsProfileCompletion');
            }

            setUserId?.(user.id);
            setUser?.(user);
            setIsLoggedIn(true);
            setView('dashboard');
        } catch (e) {
            setError(e.response?.data?.message || 'Invalid OTP');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex flex-col justify-center items-center p-6 bg-[#01a49920]">
    <div className="w-full max-w-sm bg-white p-6 rounded-3xl shadow-2xl text-center">

        {/* Logo */}
        {!logoLoading && logo && (
            <div className="mb-6 flex justify-center">
                <img
                    src={logo}
                    alt="OzNULI"
                    className="h-20 object-contain"
                />
            </div>
        )}

        {step === 'enterPhone' && (
            <>
                <h2 className="text-2xl font-bold text-[#01a499] mb-6">
                    Login with Mobile
                </h2>

                <input
                    type="tel"
                    placeholder="Enter mobile number"
                    value={phone}
                    onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '');
                        setPhone(value);
                    }}
                    maxLength={10}
                    className="w-full p-4 mb-4 border border-gray-300 rounded-xl focus:border-[#01a499] focus:ring-1 focus:ring-[#01a499]"
                />

                {error && <p className="text-red-600 mb-2">{error}</p>}

                <Button
                    onClick={handleSendOtp}
                    disabled={phone.length !== 10 || loading}
                >
                    {loading ? 'Sending...' : 'OTP'}
                </Button>
            </>
        )}

        {step === 'verifyOTP' && (
            <>
                <h2 className="text-2xl font-bold text-[#01a499] mb-6">
                    Enter OTP
                </h2>

                {/* ✅ OTP GRID (Fixed Properly) */}
                <div className="grid grid-cols-6 gap-2 mb-4 w-full">
                    {otp.map((digit, index) => (
                        <input
                            key={index}
                            type="text"
                            maxLength={1}
                            value={digit}
                            ref={(el) => (inputsRef.current[index] = el)}
                            onChange={(e) =>
                                handleOtpChange(e.target.value, index)
                            }
                            onKeyDown={(e) =>
                                handleOtpKeyDown(e, index)
                            }
                            className={`
                                w-full
                                aspect-square
                                text-center
                                text-lg
                                sm:text-xl
                                font-bold
                                border-2
                                rounded-xl
                                transition-all
                                duration-200
                                ${digit
                                    ? "border-[#01a499] bg-[#01a499]/10 text-[#01a499]"
                                    : "border-gray-300"}
                                focus:outline-none
                                focus:border-[#01a499]
                                focus:ring-2
                                focus:ring-[#01a499]/30
                            `}
                        />
                    ))}
                </div>

                {serverOtp && (
                    <p className="h-12 flex items-center justify-center 
                        text-xl font-extrabold tracking-widest
                        text-red-500">
                        OTP: {serverOtp}
                    </p>
                )}

                {error && (
                    <p className="text-red-600 mb-2">{error}</p>
                )}

                <Button
                    onClick={handleVerifyOtp}
                    disabled={otp.join('').length !== 6 || loading}
                >
                    {loading ? 'Verifying...' : 'Verify & Login'}
                </Button>

                <p className="mt-4 text-sm text-gray-500">
                    Wrong number?{" "}
                    <button
                        onClick={() => {
                            setStep('enterPhone');
                            setOtp(Array(6).fill(''));
                        }}
                        className="text-[#01a499] font-semibold"
                    >
                        Edit
                    </button>
                </p>
            </>
        )}
    </div>

    <p className="mt-6 text-center text-xs text-gray-500">
        Powered by Eco Friend Industries
    </p>
</div>
    );
};
